import { useEffect, useMemo, useRef, useState } from 'react';
import { CardCarousel } from './CardCarousel';
import type { CardItem } from './types';

const SAMPLE_CARDS: CardItem[] = [
  {
    image: '/LandingPageAssets/1.png',
    title: '1. Foundation & Mascaras',
    timestamp: 0,
  },
  {
    image: '/LandingPageAssets/2.png',
    title: '2. Talcum Powder',
    timestamp: 3,
  },
  {
    image: '/LandingPageAssets/3.png',
    title: '3. Fairness & Skin Whitening creams',
    timestamp: 6,
  },
  {
    image: '/LandingPageAssets/4.png',
    title: '4. Sunscreens',
    timestamp: 9,
  },
  {
    image: '/LandingPageAssets/creative-freedom.png',
    title: '5. Lipstick',
    timestamp: 12,
  },
];

const SAMPLE_VIDEO =
  'https://storage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4';

/**
 * Standalone demo: internal timestamp clock, and a real video driving currentTime.
 * Not wired into the product video editor.
 */
export function CardCarouselDemo() {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [videoTime, setVideoTime] = useState(0);
  const [internalPlaying, setInternalPlaying] = useState(true);
  const [aspect, setAspect] = useState<'9:16' | '16:9' | '1:1'>('9:16');

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    let raf = 0;
    let live = true;
    const tick = () => {
      if (!live) return;
      setVideoTime(video.currentTime || 0);
      raf = window.requestAnimationFrame(tick);
    };
    const onPlay = () => {
      cancelAnimationFrame(raf);
      raf = window.requestAnimationFrame(tick);
    };
    const onPause = () => cancelAnimationFrame(raf);
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    if (!video.paused) onPlay();
    return () => {
      live = false;
      cancelAnimationFrame(raf);
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
    };
  }, []);

  const stageStyle = useMemo(() => {
    if (aspect === '16:9') return { width: 'min(92vw, 880px)', aspectRatio: '16 / 9' };
    if (aspect === '1:1') return { width: 'min(72vw, 520px)', aspectRatio: '1 / 1' };
    return { width: 'min(42vw, 360px)', aspectRatio: '9 / 16' };
  }, [aspect]);

  return (
    <div className="min-h-screen bg-[#0b0e13] text-slate-100 px-6 py-8">
      <div className="max-w-6xl mx-auto space-y-8">
        <header className="space-y-2">
          <p className="text-xs uppercase tracking-[0.2em] text-teal-400">Overlay graphic</p>
          <h1 className="text-2xl font-bold">CardCarousel demo</h1>
          <p className="text-sm text-slate-400 max-w-2xl">
            Reusable video overlay. Pass <code className="text-teal-300">cards</code> and either
            let it clock itself from timestamps, or drive it with{' '}
            <code className="text-teal-300">currentTime</code> from a parent video.
          </p>
        </header>

        <div className="flex flex-wrap gap-2 text-xs">
          {(['9:16', '16:9', '1:1'] as const).map((id) => (
            <button
              key={id}
              type="button"
              onClick={() => setAspect(id)}
              className={`px-3 py-1.5 rounded-full border ${
                aspect === id
                  ? 'bg-teal-500 text-black border-teal-400'
                  : 'border-white/15 text-slate-300'
              }`}
            >
              {id}
            </button>
          ))}
        </div>

        <section className="grid lg:grid-cols-2 gap-8 items-start">
          <div>
            <h2 className="text-sm font-semibold mb-3">1. Internal timestamp timer</h2>
            <div
              className="relative overflow-hidden rounded-2xl bg-[#151b24] border border-white/10 mx-auto"
              style={stageStyle}
            >
              <div className="absolute inset-0 bg-gradient-to-b from-slate-700 to-slate-950" />
              <CardCarousel
                cards={SAMPLE_CARDS}
                isPlaying={internalPlaying}
                loop
                slideDuration={700}
                position="bottom"
              />
            </div>
            <button
              type="button"
              onClick={() => setInternalPlaying((v) => !v)}
              className="mt-3 text-xs px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15"
            >
              {internalPlaying ? 'Pause internal clock' : 'Resume internal clock'}
            </button>
          </div>

          <div>
            <h2 className="text-sm font-semibold mb-3">2. External video currentTime</h2>
            <div
              className="video-wrapper relative overflow-hidden rounded-2xl bg-black border border-white/10 mx-auto"
              style={stageStyle}
            >
              <video
                ref={videoRef}
                className="absolute inset-0 w-full h-full object-cover"
                src={SAMPLE_VIDEO}
                muted
                playsInline
                autoPlay
                loop
                controls
              />
              <CardCarousel
                cards={SAMPLE_CARDS}
                currentTime={videoTime}
                slideDuration={700}
                position="bottom"
              />
            </div>
            <p className="mt-3 text-xs text-slate-500 font-mono">
              video.currentTime = {videoTime.toFixed(2)}s
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}

export default CardCarouselDemo;
